import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LineReveal, easeOutExpo } from "@/lib/motion";

const HERO_IMAGE =
  "https://res.cloudinary.com/zoqnszkn/image/upload/f_auto,q_auto/unnamed";

const HeroCarousel = () => {
  return (
    <div className="relative h-full w-full overflow-hidden rounded-3xl" data-testid="hero-carousel">
      <img
        src={HERO_IMAGE}
        alt="Nathan — HANCOCK coaching"
        style={{
          filter: "brightness(0.95)",
          objectPosition: "center center",
        }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Subtle rounded-edge definition — no shading over the photo */}
      <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/10" />
    </div>
  );
};

export const Hero = () => {
  const navigate = useNavigate();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 min-h-screen flex flex-col lg:flex-row lg:items-center gap-12 lg:gap-16 pt-28 pb-20 lg:pt-20 lg:pb-0">
        {/* Left column — text (≈60%) */}
        <div className="w-full lg:w-[58%] flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-12 bg-white/40" />
            <span className="text-xs md:text-sm uppercase tracking-[0.28em] text-white/50">
              Online Performance Coaching
            </span>
          </motion.div>

          <h1 className="font-syne font-extrabold tracking-tightest text-white text-[10vw] leading-[0.92] md:text-[7.5vw] lg:text-[5.5vw]">
            <LineReveal
              lines={["Built on science.", <>Delivered with <span className="font-cormorant italic font-light text-white/70">precision.</span></>]}
              delay={0.5}
              stagger={0.14}
              trigger="load"
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: easeOutExpo, delay: 0.55 }}
            className="mt-7 pb-1 text-lg md:text-2xl text-white/70 font-light font-syne tracking-tight leading-relaxed"
          >
            The last coaching program you'll ever need.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: easeOutExpo, delay: 0.65 }}
            className="mt-10"
          >
            <button
              onClick={() => navigate("/apply")}
              data-testid="hero-apply-btn"
              className="group relative overflow-hidden rounded-full bg-white px-12 py-5 text-base uppercase tracking-[0.18em] text-black font-semibold shadow-[0_0_60px_-15px_rgba(255,255,255,0.5)]"
            >
              <span className="relative z-10 flex items-center gap-3">
                Apply Now
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1.5">→</span>
              </span>
              <span className="absolute inset-0 bg-white/0 transition-colors duration-300 group-hover:bg-white/90" />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: easeOutExpo, delay: 0.75 }}
            className="mt-10 max-w-xl text-base md:text-lg text-white/55 leading-relaxed font-light"
          >
            An evidence-based coaching practice for people who are done guessing.
            Engineered programming, measured nutrition, and weekly personal
            check-ins, built entirely around you.
          </motion.p>
        </div>

        {/* Right column — carousel (≈40%) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.99 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: easeOutExpo, delay: 0.2 }}
          className="w-full lg:w-[32%] shrink-0"
          style={{ aspectRatio: "1015/2050" }}
        >
          <HeroCarousel />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 1.0 }}
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-white/40"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={16} strokeWidth={1.2} />
        </motion.div>
      </motion.div>
    </section>
  );
};
